// Client-side index/cache of the SHGM zone dataset so the planning map opens FULL instantly instead of
// re-fetching per view. The whole country (~11k zones + ~1.3k NOTAMs, ~23 MB) is fetched once, stored in
// IndexedDB (too big for localStorage), and served from there on subsequent opens; a background refresh
// keeps it current. Parsing/normalization stays in the shared engine (engine.ts, via fetch.ts).

import { fetchAllZones, fetchAllNotams, fetchSnapshotIdentity, usingSnapshot } from './fetch.ts';
import type { Zone, Notam } from './engine.ts';
import type { EffectiveGeom } from './merge.ts';

const DB_NAME = 'drone-planning';
const STORE = 'dataset';
const KEY = 'shgm-zones-p7'; // -p7: entries carry `sourceKey` and `notamsLoaded`
// (an older entry has neither, and reading a missing `notamsLoaded` as 'the NOTAMs are fine' is
// exactly the failure it exists to prevent — so bump the key rather than migrate). // -p6: entries carry `dataAt`; an older entry has none, and reading its
// absence as 'fresh' is the exact failure this field prevents — so bump the key rather than migrate. // -p5: geometry now fetched at ~1 m precision (smaller). Bump ⇒ old big cache is replaced.
/** Serve cache immediately; refresh in the background when older than this. */
export const MAX_AGE_MS = 12 * 60 * 60 * 1000; // 12 h

export interface CachedDataset {
  zones: Zone[];
  notams: Notam[];
  /** When THIS BROWSER downloaded it. Cache management only — never show this to a user. */
  fetchedAt: number;
  /**
   * When the DATA was produced, epoch ms, or null when that cannot be established.
   *
   * Against the live ArcGIS these are the same instant, so this is set to `fetchedAt`. Against a
   * pre-baked snapshot they are unrelated, and this is the manifest's `bakedAt`. Null means unknown,
   * and unknown must be treated as unsafe rather than as current.
   */
  dataAt: number | null;
  /**
   * Which published objects this dataset was built from, or null on the live-service path. Lets a
   * client ask "is there a newer bake?" for the price of the manifest instead of the whole country.
   */
  sourceKey: string | null;
  /**
   * Whether the NOTAM layer actually loaded. False means the permanent zones are usable and the
   * temporary ones are absent — which must read as "withheld", never as "there are none".
   */
  notamsLoaded: boolean;
}

// One memoized connection reused across reads/writes — opening a fresh one per call (on every mount +
// hourly refresh) leaked connections and would block a future schema-version bump.
let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null; // let the next call retry
      reject(req.error);
    };
  });
  return dbPromise;
}

export async function readCache(): Promise<CachedDataset | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as CachedDataset | undefined) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null; // IndexedDB unavailable (private mode etc.) → fall back to a live fetch
  }
}

async function writeCache(data: CachedDataset): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(data, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort cache; a failed write just means we refetch next time */
  }
}

/**
 * Fetch the full dataset and persist it.
 *
 * The two layers fail INDEPENDENTLY. `Promise.all` used to bind them, so a NOTAM object that 404'd
 * — or a manifest naming no NOTAM snapshot — rejected the whole refresh: nothing was cached and
 * nothing rendered, not even the zones that had downloaded perfectly, and a first-ever visitor got
 * the red "this map is NOT reliable" banner naming the layer that had in fact worked.
 *
 * Zones stay required, because a map without them is not a map. NOTAMs degrade to withheld, which
 * is a state the UI already knows how to say.
 */
export async function refreshDataset(signal?: AbortSignal, onProgress?: (n: number) => void): Promise<CachedDataset> {
  const [zones, notamResult, identity] = await Promise.all([
    fetchAllZones(signal, onProgress),
    fetchAllNotams(signal).then(
      (notams) => ({ ok: true as const, notams }),
      (error: unknown) => {
        if (signal?.aborted) throw error; // an abort is not a layer failure
        return { ok: false as const, error };
      },
    ),
    fetchSnapshotIdentity(),
  ]);
  if (!notamResult.ok) {
    console.warn('[planning] temporary restrictions unavailable; the permanent zones still loaded', notamResult.error);
  }
  const now = Date.now();
  // Live service: the data is as old as the request. Snapshot: only the manifest knows, and if it
  // could not be read the age is unknown rather than now.
  const data: CachedDataset = {
    zones,
    notams: notamResult.ok ? notamResult.notams : [],
    notamsLoaded: notamResult.ok,
    fetchedAt: now,
    dataAt: usingSnapshot() ? identity.bakedAt : now,
    sourceKey: identity.key,
  };
  await writeCache(data);
  return data;
}

/**
 * Has a NEW bake been published since this cache was built?
 *
 * Returns a touched cache entry when nothing has changed, and null when the caller should do a full
 * refetch. This exists because the two clocks below do not agree: the refresh gate measures when
 * THIS BROWSER downloaded, and the trust gate measures when the DATA was made, so the trust gate
 * always trips first by the bake→download lag. Without a cheap way to look, closing that gap would
 * mean refetching several megabytes every hour to discover that nothing had changed.
 */
export async function revalidate(d: CachedDataset): Promise<CachedDataset | null> {
  if (!usingSnapshot() || !d.sourceKey) return null;
  const identity = await fetchSnapshotIdentity();
  if (!identity.key || identity.key !== d.sourceKey) return null; // new bake, or unknown → refetch
  const touched: CachedDataset = { ...d, fetchedAt: Date.now(), dataAt: identity.bakedAt };
  await writeCache(touched);
  return touched;
}

/**
 * How old the DATA may be before temporary restrictions stop being trustworthy.
 *
 * Permanent zones tolerate weeks. NOTAMs do not: they are issued and expire continuously, and a
 * snapshot's real defect is not the expired areas it contains — those are filtered out on render —
 * but the newly issued ones it is MISSING, which no client-side filter can recover. The bake runs
 * every three hours, so this tolerates several consecutive failures before the layer withdraws.
 */
export const NOTAM_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/** Age of the DATA in ms, or null when unknown. Unknown must fail closed at the call site. */
export const dataAgeMs = (d: CachedDataset): number | null =>
  d.dataAt == null ? null : Math.max(0, Date.now() - d.dataAt);

/** Whether temporary restrictions may be drawn at all. Absent, unknown or too old → no. */
export function notamsTrustworthy(d: CachedDataset): boolean {
  if (!d.notamsLoaded) return false; // the layer never arrived; absence is not evidence of none
  const age = dataAgeMs(d);
  return age !== null && age <= NOTAM_MAX_AGE_MS;
}

/**
 * How old the DATA may get before the client goes looking for a newer bake.
 *
 * Half the trust window, so the client tries to refresh well BEFORE it would have to withdraw the
 * NOTAM layer. It used to look only at `fetchedAt`, and since `dataAt <= fetchedAt` always, the
 * trust gate crossed twelve hours first: the layer withdrew itself while the client sat on a cache
 * it considered fresh and declined to refetch — sometimes for hours after a new bake was already on
 * the CDN. The `revalidate` path above is what makes checking this often affordable.
 */
export const DATA_REFRESH_AGE_MS = NOTAM_MAX_AGE_MS / 2;

export const isStale = (d: CachedDataset): boolean => {
  if (Date.now() - d.fetchedAt > MAX_AGE_MS) return true;
  if (!usingSnapshot()) return false; // live service: download time IS data time, nothing more to ask
  const age = dataAgeMs(d);
  return age === null || age > DATA_REFRESH_AGE_MS;
};

// The effective-mode geometry (the ~3 s country-wide union) persisted across sessions, keyed by the dataset
// `stamp` (fetchedAt), so a reload with unchanged data skips both the recompute AND the big worker
// postMessage. It's VISUAL only — the fly/no-fly assessment always uses the live zone/NOTAM grids — so a
// persisted merge being up to the dataset's refresh window stale on temporary NOTAMs is not a safety issue.
const MERGE_KEY = 'shgm-merge-p7'; // -p7: entries carry validUntil (the NOTAM active-window edge)
// `validUntil` is the instant the merge stops describing the airspace: the next NOTAM window edge
// after the merge was computed (engine.ts nextNotamChange), or null when no NOTAM edge remains. The
// dataset id alone is not enough to key this. computeEffective() takes `now` and drops NOTAMs outside
// their window, so a merge persisted under an unchanged id keeps yesterday's answer to a question
// whose answer moved — for as long as that id stands, which is up to the 12 h NOTAM trust window.
const MERGE_KEY_OLD = ['shgm-merge-p6'];
interface CachedMerge { stamp: number; geom: EffectiveGeom; validUntil: number | null }
export async function readMerge(stamp: number): Promise<EffectiveGeom | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(MERGE_KEY);
      req.onsuccess = () => {
        const v = req.result as CachedMerge | undefined;
        if (!v || v.stamp !== stamp) return resolve(null);
        if (v.validUntil != null && Date.now() >= v.validUntil) return resolve(null); // a window edge passed → recompute
        resolve(v.geom);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}
export async function writeMerge(stamp: number, geom: EffectiveGeom, validUntil: number | null): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const os = tx.objectStore(STORE);
      for (const k of MERGE_KEY_OLD) os.delete(k); // the superseded record is megabytes; don't leave it behind
      os.put({ stamp, geom, validUntil } satisfies CachedMerge, MERGE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}
